import type { AccessCatalog, TierKey } from "types/interfaces/access";

export const GUIDE_TIERS = ["view", "contribute", "manage"] as const;
export type GuideTier = (typeof GUIDE_TIERS)[number];

/** "yes": the tier allows it; "no": the page has the tier, which does not allow
 * it; "unused": the page has no such tier. */
export type GuideCell = "yes" | "no" | "unused";

export interface GuideRow {
  key: string;
  label: string;
  cells: Record<GuideTier, GuideCell>;
}

export interface GuideSection {
  key: string;
  name: string;
  note: string | null;
  rows: GuideRow[];
}

const RANK: Record<TierKey, number> = {
  none: 0,
  view: 1,
  contribute: 2,
  manage: 3,
};

const TIER_NAME: Record<TierKey, string> = {
  none: "No access",
  view: "View",
  contribute: "Contribute",
  manage: "Manage",
};

/** The tier guide: per page, what each tier allows, from the server's catalog -
 * the same actions the page's endpoints and buttons ask. */
export function tierGuide(catalog: AccessCatalog): GuideSection[] {
  return catalog.surfaces.map((surface) => {
    const supported = new Set(surface.tiers.map((spec) => spec.tier));
    const parent = surface.parent
      ? catalog.surfaces.find((other) => other.key === surface.parent)
      : undefined;
    let note: string | null = null;
    if (surface.everyone) {
      note = "Open to every user, whatever the group.";
    } else if (parent && parent.tiers.length > 0) {
      note = `A tab of ${parent.name}: needs at least ${
        TIER_NAME[parent.tiers[0].tier]
      } there.`;
    }
    const rows = (surface.actions ?? []).map((action) => {
      const cells = {} as Record<GuideTier, GuideCell>;
      for (const tier of GUIDE_TIERS) {
        if (!supported.has(tier)) cells[tier] = "unused";
        else cells[tier] = RANK[tier] >= RANK[action.tier] ? "yes" : "no";
      }
      return { key: action.key, label: action.label, cells };
    });
    return { key: surface.key, name: surface.name, note, rows };
  });
}

export function tierName(tier: TierKey): string {
  return TIER_NAME[tier];
}
